import { useState, useRef, useEffect } from "react";
import {
    SettingsIcon,
    CalendarIcon,
    InfoIcon,
    BracesIcon,
    XIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    Loader2Icon,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Dialog, DialogContent } from "@component/dialog";
import { Tooltip } from "@component/tooltip";
import { Checkbox } from "@component/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { SendCustomizedEmailRequest } from "@type/candidate";
import { Applicant } from "@type/applicant";
import { Job, ReviewScheduledNoteItem } from "@type/job";
import { EmailTemplate } from "@type/setting";
import { useUserProfileContext } from "@context/user_profile";
import EmailVariablesPanel from "@container/email-variable-panel";
import TextEditor, { TextEditorRef } from "./rich-editor/v2/text-editor";
import { getEmailTemplates } from "@service/employer";
import { defaultAssessmentInvitationTemplate, defaultHireTemplate, defaultInterviewInvitationTemplate, defaultJobInvitationTemplate, defaultRejectTemplate } from "@constant/email";
import { mergeEmailTemplates } from "@util/email";
import { Link } from "react-router-dom";
import { getCompanyInfo } from "@service/user";
import { Company } from "@type/company";
import { Textarea } from "./textarea";
import { debounce } from "lodash";

interface Props {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    applicants?: Applicant[]
    job: Job | null;
    showInterviewRoleSelector?: boolean;
    onSendInvitation: (body: SendCustomizedEmailRequest, reviewScheduledNotes: ReviewScheduledNoteItem) => void | Promise<void>;
}

export default function InterviewInvitationDialogV2({
    isOpen,
    onClose,
    applicants = [],
    job,
    showInterviewRoleSelector = false,
    onSendInvitation
}: Props) {
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
    const [listTemplates, setListTemplates] = useState<EmailTemplate[]>([]);
    const [senderName, setSenderName] = useState("ourteam");
    const [replyTo, setReplyTo] = useState<string>("work@ourteam.ai");
    const [ccEmail, setCCEmail] = useState<string>("")

    const [lastFocusedField, setLastFocusedField] = useState<"subject" | "content">("content");
    const [showVariablesPanel, setShowVariablesPanel] = useState(false);
    const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);
    const [selectedApplicants, setSelectedApplicants] = useState<Applicant[]>([]);
    const [isReviewDetailsExpanded, setIsReviewDetailsExpanded] = useState(true);
    const [reviewType, setReviewType] = useState<string>("interview");
    const [reviewName, setReviewName] = useState<string>("");
    const [interviewType, setInterviewType] = useState<string>("recruiter");
    const [interviewDate, setInterviewDate] = useState<string>("");
    const [interviewNotes, setInterviewNotes] = useState("");

    // Refs for form elements
    const subjectInputRef = useRef<HTMLInputElement>(null);
    const contentEditorRef = useRef<TextEditorRef>(null);

    const reloadTemplates = () => {
        setIsLoadingData(true);
        getEmailTemplates().then((resp) => {
            if (resp.status === 200) {
                const hardcodedDefaults = [
                    {
                        template_id: "default_reject",
                        template_name: "Standard Rejection Email",
                        template_category: "reject",
                        email_subject: defaultRejectTemplate(companyInfo).subject,
                        email_content: defaultRejectTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_hire",
                        template_name: "Standard Job Offer Email",
                        template_category: "hire",
                        email_subject: defaultHireTemplate(companyInfo).subject,
                        email_content: defaultHireTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_interview_invitation",
                        template_name: "Standard Interview Invitation Email",
                        template_category: "interview-invitation",
                        email_subject: defaultInterviewInvitationTemplate(companyInfo).subject,
                        email_content: defaultInterviewInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_job_invitation",
                        template_name: "Standard Job Invitation Email",
                        template_category: "job-invitation",
                        email_subject: defaultJobInvitationTemplate(companyInfo).subject,
                        email_content: defaultJobInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_assessment_invitation",
                        template_name: "Standard Screening Invitation Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentInvitationTemplate(7).subject,
                        email_content: defaultAssessmentInvitationTemplate(7).content,
                        is_default: true,
                    },
                ];
                const merged = mergeEmailTemplates(resp.data.data, hardcodedDefaults);
                setListTemplates(merged);
                setSelectedTemplate(merged.find(t => t.template_id === "default_interview_invitation") ?? null);
            }
        }).finally(() => setIsLoadingData(false))
    }

    useEffect(() => {
        if (isOpen) {
            reloadTemplates()
            setSelectedApplicants(applicants)
        }
        return () => {
            setSelectedTemplate(null);
            setIsLoading(false);
            setIsLoadingData(false);
        }
    }, [isOpen])


    useEffect(() => {
        if (isOpen && !isLoadingUserProfile && userInfo) {
            getCompanyInfo(userInfo.company_id).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                    setSenderName(job?.outgoing_email?.sender_name ? job.outgoing_email.sender_name : resp.data.data.company_metadata?.outgoing_email?.sender_name || "ourteam");
                    setReplyTo(job?.outgoing_email?.default_reply_to ? job.outgoing_email.default_reply_to : resp.data.data.company_metadata?.outgoing_email?.default_reply_to || "work@ourteam.ai");
                }
            })
        }
    }, [isOpen, isLoadingUserProfile])


    const handleTemplateChange = (templateID: string) => {
        const template = listTemplates.find(t => t.template_id === templateID);
        if (template) {
            setSelectedTemplate(template);
        } else {
            setSelectedTemplate(null);
        }
    };

    const handleInsertVariable = (variable: string) => {
        // Get references to both fields using refs
        const subjectInput = subjectInputRef.current;
        const contentEditor = contentEditorRef.current;

        // Insert into the last focused field
        if (lastFocusedField === "subject" && subjectInput) {
            const start = subjectInput.selectionStart || 0;
            const end = subjectInput.selectionEnd || 0;
            const newSubject =
                selectedTemplate?.email_subject.substring(0, start) +
                variable +
                selectedTemplate?.email_subject.substring(end);
            setSelectedTemplate((prev) => prev ? { ...prev, email_subject: newSubject } : null);

            // Set cursor position after the inserted variable
            setTimeout(() => {
                subjectInput.focus();
                subjectInput.setSelectionRange(
                    start + variable.length,
                    start + variable.length
                );
            }, 0);
        } else if (contentEditor) {
            // Insert text at current cursor position in the rich text editor
            contentEditor.insertText(variable);

            // Focus the editor after insertion
            setTimeout(() => {
                contentEditor.focus();
            }, 0);
        } else {
            // Fallback: append to end of content
            setSelectedTemplate((prev) => prev ? { ...prev, email_content: (prev.email_content + " " + variable) } : null);
        }
    }

    const handleSendInvitation = async () => {
        setIsLoading(true);
        try {
            await onSendInvitation(
                {
                    user_ids: applicants.map(applicant => applicant.applicant_id) || [],
                    sender_name: senderName,
                    cc: ccEmail,
                    reply_to: replyTo,
                    email_subject: selectedTemplate?.email_subject || "",
                    email_body: selectedTemplate?.email_content || "",
                    attachment: null,
                    job_id: job?.id || "",
                    interview_invitation: true,
                    reject_candidate: false,
                    applicant_ids: applicants.map(applicant => applicant.id) || [],
                },
                {
                    review_type: reviewType,
                    review_name: reviewName,
                    interview_type: interviewType,
                    interview_date: interviewDate,
                    notes: interviewNotes,
                    action_by: userInfo?.first_name || "",
                    action_at: new Date().toISOString(),
                }
            );
        } finally {
            setIsLoading(false);
        }
    };

    const listSelectedApplicantEmails = selectedApplicants.filter(applicant => applicant.is_selected) || [];

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[1400px] w-[98vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
                {/* Single Unified Container */}
                <div className="flex flex-col flex-1 min-h-0 p-6">
                    {/* Header - Fixed */}
                    <div className="mb-6 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">
                                    Send Review Invitation
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {`This will send an interview invitation email to ${listSelectedApplicantEmails.length} candidate${listSelectedApplicantEmails.length !== 1 ? "s" : ""}.`}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content - Flexible Layout */}
                    <div className="flex flex-1 min-h-0 gap-6 overflow-hidden">
                        {/* Left Column - Form Fields */}
                        <div
                            className={`${showVariablesPanel ? "w-1/3" : "w-1/2"} space-y-5 overflow-y-auto pr-2`}
                            style={{ paddingRight: "8px", paddingLeft: "8px" }}
                        >
                            <div>
                                <Label
                                    htmlFor="recipient-emails"
                                    className="text-sm font-medium text-foreground"
                                >
                                    Recipients ({listSelectedApplicantEmails.length})
                                </Label>

                                {/* Email tags container */}
                                <div className="mt-2 min-h-[46px] border border-input rounded-md p-2 flex flex-wrap gap-2 items-center bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                    {/* Individual email tags */}
                                    {listSelectedApplicantEmails.map((applicant, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-md text-sm"
                                        >
                                            <span>{applicant.main_email}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedApplicants((prev) =>
                                                        prev.map((item) =>
                                                            item.applicant_id === applicant.applicant_id
                                                                ? { ...item, is_selected: false }
                                                                : item
                                                        )
                                                    );
                                                }}
                                                className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                                            >
                                                <XIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Sender Information */}
                            <div className="grid grid-cols-1 gap-5">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Label
                                            htmlFor="sender-name"
                                            className="text-sm font-medium text-foreground"
                                        >
                                            Sender Name
                                        </Label>
                                        <Tooltip side="right" content={(
                                            <p>
                                                This name appears as the email sender. It's
                                                prefilled based on your company's default Email
                                                Settings.
                                            </p>
                                        )}>
                                            <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                        </Tooltip>
                                    </div>
                                    <Input
                                        id="sender-name"
                                        value={senderName}
                                        onChange={(e) => setSenderName(e.target.value)}
                                        className="mt-2 text-foreground"
                                    />
                                </div>
                                <div>
                                    <Label
                                        htmlFor="sender-email"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Sender Email
                                    </Label>
                                    <Input
                                        id="sender-email"
                                        value="work@ourteam.ai"
                                        disabled
                                        className="mt-2 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                                    />
                                </div>
                            </div>

                            {/* Reply To */}
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="reply-to"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Reply To
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>
                                            Candidate replies go to this address. You can update
                                            the default in Email Settings.
                                        </p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Input
                                    id="reply-to"
                                    value={replyTo}
                                    onChange={(e) => setReplyTo(e.target.value)}
                                    className="mt-2 text-foreground"
                                />
                            </div>

                            {/* CC Emails */}
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="cc-emails"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        CC Emails
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>Separate multiple email addresses with a comma.</p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Input
                                    id="cc-emails"
                                    value={ccEmail}
                                    onChange={(e) => setCCEmail(e.target.value)}
                                    placeholder="Add CC emails..."
                                    className="mt-2 text-foreground placeholder:text-muted-foreground"
                                />
                            </div>

                            {/* Template Selector */}
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label className="text-sm font-medium text-foreground">
                                        Email Template
                                    </Label>
                                    <Tooltip side="right" content={(<p>You can edit this email template but the original template won't be affected.</p>)}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <Select
                                        value={selectedTemplate?.template_id || ""}
                                        onValueChange={handleTemplateChange}
                                    >
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder="Choose a template" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listTemplates.filter((template) => template.template_category === 'interview-invitation').length === 0 ? (
                                                <SelectItem value="none" disabled>
                                                    No templates available
                                                </SelectItem>
                                            ) : (
                                                <>
                                                    {listTemplates.filter((template) => template.template_category === 'interview-invitation').map((template, key) => (
                                                        <SelectItem key={key} value={template.template_id || ""}>
                                                            <div className="flex items-center justify-between w-full">
                                                                <span>{template.template_name}</span>
                                                                {template.is_default && (
                                                                    <span
                                                                        className="ml-2 px-2 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full"
                                                                        style={{
                                                                            paddingTop: "4px",
                                                                            paddingBottom: "4px",
                                                                        }}
                                                                    >
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>



                            {showInterviewRoleSelector && (
                                <div className="bg-white dark:bg-gray-900 border border-blue-50 dark:border-blue-900/30 rounded-lg overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setIsReviewDetailsExpanded(!isReviewDetailsExpanded)
                                        }
                                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-2">
                                            <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />

                                            <h4 className="text-sm font-semibold text-foreground">
                                                Internal Review Note
                                            </h4>
                                        </div>
                                        {isReviewDetailsExpanded ? (
                                            <ChevronUpIcon className="w-4 h-4 text-muted-foreground" />
                                        ) : (
                                            <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                                        )}
                                    </button>

                                    {isReviewDetailsExpanded && (
                                        <div className="px-4 pb-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-foreground">
                                                        Review Type
                                                        <span className="text-red-500 ml-1">*</span>
                                                    </label>
                                                    <Select
                                                        value={reviewType}
                                                        onValueChange={(
                                                            value: "interview" | "case_study"
                                                        ) => {
                                                            setReviewType(value);
                                                        }}
                                                    >
                                                        <SelectTrigger className="h-10 bg-white dark:bg-gray-800">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="interview">Interview</SelectItem>
                                                            <SelectItem value="case_study">
                                                                Case Study
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {reviewType === "interview" && (
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium text-foreground">
                                                            Interview Type
                                                            <span className="text-red-500 ml-1">*</span>
                                                        </label>
                                                        <Select
                                                            value={interviewType}
                                                            onValueChange={(value) =>
                                                                setInterviewType(
                                                                    value as
                                                                    | "recruiter"
                                                                    | "hiring_manager"
                                                                    | "final"
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger className="h-10 bg-white dark:bg-gray-800">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="recruiter">
                                                                    Recruiter
                                                                </SelectItem>
                                                                <SelectItem value="hiring_manager">
                                                                    Hiring Manager
                                                                </SelectItem>
                                                                <SelectItem value="final">Final</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-foreground">
                                                    Review Name
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <Input
                                                    value={reviewName}
                                                    onChange={(e) => setReviewName(e.target.value)}
                                                    placeholder={
                                                        reviewType === "interview"
                                                            ? "e.g., Technical Interview with John"
                                                            : "e.g., Product Case Study"
                                                    }
                                                    className="h-10 bg-white dark:bg-gray-800"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-foreground">
                                                    Interview Date
                                                    <span className="text-red-500 ml-1">*</span>
                                                </label>
                                                <Input
                                                    type="date"
                                                    value={interviewDate}
                                                    onChange={(e) => setInterviewDate(e.target.value)}
                                                    min={new Date().toISOString().split("T")[0]}
                                                    className="h-10 bg-white dark:bg-gray-800"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-foreground">
                                                    Purpose or Notes
                                                </label>
                                                <Textarea
                                                    value={interviewNotes}
                                                    onChange={(e) => setInterviewNotes(e.target.value)}
                                                    placeholder="e.g., evaluate problem solving approach, culture fit, or decision readiness"
                                                    className="min-h-[80px] resize-y bg-white dark:bg-gray-800"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right Column - Email Content */}
                        <div
                            className={`${showVariablesPanel ? "w-1/3" : "w-1/2"} flex flex-col min-h-0 pl-2 overflow-y-auto`}
                            style={{ paddingRight: "8px" }}
                        >
                            {/* Subject */}
                            <div className="mb-4">
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="email-subject"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Email Subject
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>Personalize your email subject using variables.</p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Input
                                    ref={subjectInputRef}
                                    value={selectedTemplate?.email_subject || ""}
                                    onChange={(e) => setSelectedTemplate((prev) => prev ? { ...prev, email_subject: e.target.value } : null)}
                                    onFocus={() => setLastFocusedField("subject")}
                                    disabled={!selectedTemplate}
                                    className="mt-2 text-foreground"
                                />
                            </div>

                            <div className="flex items-center justify-between mb-2">
                                <Label
                                    htmlFor="email-content"
                                    className="text-sm font-medium text-foreground mb-2 flex-shrink-0"
                                >
                                    Email Content
                                </Label>
                                <button
                                    type="button"
                                    onClick={() => setShowVariablesPanel(!showVariablesPanel)}
                                    className="text-xs text-blue-600 hover:text-black flex items-center gap-1"
                                >
                                    <BracesIcon className="h-3 w-3" />
                                    {showVariablesPanel ? "Hide" : "Show"} Variables
                                </button>
                            </div>
                            <TextEditor
                                ref={contentEditorRef}
                                defaultValue={selectedTemplate?.email_content || ""}
                                disabled={!selectedTemplate}
                                onChange={(content) => {
                                    setSelectedTemplate((prev) => prev ? { ...prev, email_content: content } : null);
                                }}
                                onFocus={() => setLastFocusedField("content")}
                            />
                            <p
                                className={`text-xs mt-3 flex-shrink-0 text-muted-foreground`}
                            >
                                {showVariablesPanel
                                    ? "Click on Variables in the right panel to insert them to your email subject or content."
                                    : "Click the Variables button to see all available personalization options for your email subject or content."}
                            </p>
                        </div>

                        {/* Variables Panel */}
                        {showVariablesPanel && (
                            <div
                                data-variables-panel
                                className="w-1/3 flex-shrink-0 min-h-0 flex flex-col"
                            >
                                <EmailVariablesPanel
                                    onClose={() => setShowVariablesPanel(false)}
                                    onInsertVariable={handleInsertVariable}
                                    isEditMode={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer - Fixed */}
                    <div className="mt-6 pt-4 border-t border-border flex-shrink-0">

                        <div className="flex items-center justify-between">
                            <Tooltip
                                side="right"
                                content={(
                                    <p>Manage your default email settings and templates.</p>
                                )}
                            >
                                <Link target="_blank" to="/setting?tab=email-communication&template=interview-invitation" className="h-auto p-0 text-blue-600 hover:text-blue-700 hover:bg-transparent dark:text-blue-400 dark:hover:text-blue-300 font-normal text-sm hover:underline flex items-center gap-1.5">
                                    <SettingsIcon className="w-3.5 h-3.5" />
                                    Edit Email Settings
                                </Link>
                            </Tooltip>
                            <div className="flex gap-3">
                                <Button variant="outline" disabled={isLoading} onClick={() => onClose(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={debounce(handleSendInvitation, 300)}
                                    disabled={isLoading || isLoadingData || senderName.trim() === "" || !selectedTemplate || listSelectedApplicantEmails.length === 0 || reviewName === "" || interviewDate === ""}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                                    style={{ paddingRight: "16px", paddingLeft: "16px" }}
                                >
                                    {isLoading ? (
                                        <><Loader2Icon className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                                    ) : "Send Invitation"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
